<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { SupportedLocale } from '@/i18n';
import type { PageDetailRow, PageItem } from '@/db/content';
import { useContentStore } from '@/stores/content';
import { getRootPathsAndDefaultLocale, getUrlForItem } from '@/navTree';
import type { ApiImage } from '@/types/images';
import { isOptimisedImage } from '@/types/images';
import type { ContentBlockItem } from '@/components/contentBlocks';
import FeaturedNewsArticleCard from '@/components/blocks/FeaturedNewsArticleCard.vue';
import { getReadingTimeForPageContent } from '@/utils/pageContentPlainText';
import { stripHtmlToText, normalizeCmsPlainText, normalizeWagtailRichTextHtml } from '@/utils/html';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';
import { getSortedNewsArticles } from '@/utils/newsArticles';

const PAGE_SIZE = 10;

const props = defineProps<{
  page: PageDetailRow;
}>();

const route = useRoute();
const router = useRouter();
const contentStore = useContentStore();
const { t, locale } = useI18n();

const listingItems = computed(() => contentStore.expandedListingItems);

const navRoots = computed(() =>
  getRootPathsAndDefaultLocale(listingItems.value)
);
const rootPathByLocale = computed(() => navRoots.value.rootPathByLocale);
const defaultLocale = computed(() => navRoots.value.defaultLocale);

/** Sorted by date (latest first), then by id. */
const sortedArticles = computed(() => {
  const indexPath = (props.page?.meta as { path?: string })?.path ?? '';
  return getSortedNewsArticles(listingItems.value, locale.value as SupportedLocale, indexPath);
});

const queryQ = computed(() => (route.query.q as string)?.trim() ?? '');
const queryCategory = computed(() => (route.query.category as string) ?? '');
const queryTag = computed(() => (route.query.tag as string) ?? '');
const currentPage = computed(() => Math.max(1, parseInt(String(route.query.page), 10) || 1));

const featuredArticle = computed(() => {
  if (queryQ.value || queryCategory.value || queryTag.value) return null;
  return sortedArticles.value[0] ?? null;
});

const articlesForResults = computed(() => {
  const featured = featuredArticle.value;
  if (!featured) return sortedArticles.value;
  return sortedArticles.value.filter((item) => item.id !== featured.id);
});

/** Filtered by search and category/tag. */
const filteredArticles = computed(() => {
  let list = articlesForResults.value;
  const q = queryQ.value;
  if (q) {
    const lower = q.toLowerCase();
    list = list.filter(
      (item) =>
        normalizeCmsPlainText(item.title ?? '').toLowerCase().includes(lower) ||
        stripHtmlToText(item.rich_title ?? '').toLowerCase().includes(lower) ||
        normalizeCmsPlainText(item.meta?.search_description ?? '').toLowerCase().includes(lower)
    );
  }
  if (queryCategory.value) {
    list = list.filter((item) => item.category?.slug === queryCategory.value);
  }
  if (queryTag.value) {
    list = list.filter((item) =>
      item.tags?.some((t) => t.slug === queryTag.value)
    );
  }
  return list;
});

const totalFiltered = computed(() => filteredArticles.value.length);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalFiltered.value / PAGE_SIZE))
);
const showingFrom = computed(() => {
  if (totalFiltered.value === 0) return 0;
  return (currentPage.value - 1) * PAGE_SIZE + 1;
});
const showingTo = computed(() => {
  if (totalFiltered.value === 0) return 0;
  return Math.min(currentPage.value * PAGE_SIZE, totalFiltered.value);
});
const paginatedArticles = computed(() => {
  const list = filteredArticles.value;
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return list.slice(start, start + PAGE_SIZE);
});

const newsIndexListRoot = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(newsIndexListRoot, () =>
  paginatedArticles.value.map((i) => i.rich_title ?? '').join('\u0000')
);

/** Minutes from client-side Wagtail-aligned metrics; filled after detail fetch (listing has no `content`). */
const readingMinutesById = ref<Record<number, number>>({});
let readingTimeLoadGeneration = 0;

watch(
  [paginatedArticles, locale],
  async () => {
    const ids = paginatedArticles.value.map((item) => item.id);
    const gen = ++readingTimeLoadGeneration;
    if (!ids.length) {
      readingMinutesById.value = {};
      return;
    }
    const lang = String(locale.value);
    const entries = await Promise.all(
      ids.map(async (id) => {
        const detail = await contentStore.getPageDetail(id);
        if (!detail) return { id, minutes: undefined as number | undefined };
        const raw = (detail as { content?: ContentBlockItem[] }).content;
        const minutes = Array.isArray(raw) ? getReadingTimeForPageContent(lang, raw) : 0;
        return { id, minutes };
      })
    );
    if (gen !== readingTimeLoadGeneration) return;
    const next: Record<number, number> = {};
    for (const { id, minutes } of entries) {
      if (minutes !== undefined) next[id] = minutes;
    }
    readingMinutesById.value = next;
  },
  { immediate: true }
);

const uniqueCategories = computed(() => {
  const seen = new Set<string>();
  const out: { slug: string; name: string }[] = [];
  for (const item of sortedArticles.value) {
    const c = item.category;
    if (c?.slug && !seen.has(c.slug)) {
      seen.add(c.slug);
      out.push({ slug: c.slug, name: c.name ?? c.slug });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
});

const uniqueTags = computed(() => {
  const seen = new Set<string>();
  const out: { slug: string; name: string }[] = [];
  for (const item of sortedArticles.value) {
    for (const t of item.tags ?? []) {
      if (t.slug && !seen.has(t.slug)) {
        seen.add(t.slug);
        out.push({ slug: t.slug, name: t.name ?? t.slug });
      }
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
});

function urlForItem(item: PageItem) {
  return getUrlForItem(item, rootPathByLocale.value, defaultLocale.value);
}

function formatDate(isoDate: string | null | undefined) {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function imageUrl(item: PageItem): string | undefined {
  const img = (item as { image?: ApiImage }).image;
  if (!img) return undefined;
  if (isOptimisedImage(img)) return img.fallback?.url ?? undefined;
  return 'url' in img ? (img as { url?: string }).url ?? undefined : undefined;
}

function imageAlt(item: PageItem) {
  const img = (item as { image?: ApiImage }).image;
  const richPlain = stripHtmlToText(item.rich_title ?? '');
  return (img?.title ?? (richPlain || item.title || '')) as string;
}

function applyFilters(opts: { q?: string; category?: string; tag?: string; page?: number }) {
  const query: Record<string, string> = {};
  if (opts.q !== undefined && opts.q !== '') query.q = opts.q;
  if (opts.category) query.category = opts.category;
  if (opts.tag) query.tag = opts.tag;
  if (opts.page != null && opts.page > 1) query.page = String(opts.page);
  router.push({ path: route.path, query });
}

watch(
  [totalPages, currentPage, totalFiltered],
  () => {
    const tp = totalPages.value;
    const cp = currentPage.value;
    const tf = totalFiltered.value;
    if (tf === 0) {
      if (cp > 1) {
        applyFilters({
          q: queryQ.value,
          category: queryCategory.value,
          tag: queryTag.value,
          page: 1,
        });
      }
      return;
    }
    if (cp > tp) {
      applyFilters({
        q: queryQ.value,
        category: queryCategory.value,
        tag: queryTag.value,
        page: tp,
      });
    }
  },
  { flush: 'post' }
);
</script>

<template>
  <div class="news-index" role="main">
    <div v-if="featuredArticle" class="news-index__featured">
      <FeaturedNewsArticleCard :article="featuredArticle" />
    </div>

    <div class="news-index__filters-card">
      <div class="news-index__filters-head">
        <h2 class="news-index__filters-title">
          <svg
            class="news-index__filters-title-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="m21 21-4.3-4.3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ t('news.findNews') }}
        </h2>
      </div>
      <div class="news-index__filters-body">
        <div class="news-index__filters-row">
          <form
            class="news-index__search"
            @submit.prevent="applyFilters({ q: queryQ, category: queryCategory, tag: queryTag, page: 1 })"
          >
            <label for="news-search" class="news-index__label">{{ t('news.searchLabel') }}</label>
            <div class="news-index__input-wrap">
              <svg
                class="news-index__input-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="m21 21-4.3-4.3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <input
                id="news-search"
                :value="queryQ"
                type="search"
                class="news-index__input"
                :placeholder="t('news.searchPlaceholder')"
                autocomplete="off"
                @input="applyFilters({ q: ($event.target as HTMLInputElement).value, category: queryCategory, tag: queryTag, page: 1 })"
              />
            </div>
          </form>
          <div v-if="uniqueCategories.length" class="news-index__filter">
            <label for="news-category" class="news-index__label">{{ t('news.category') }}</label>
            <select
              id="news-category"
              :value="queryCategory"
              class="news-index__select"
              @change="applyFilters({ q: queryQ, category: ($event.target as HTMLSelectElement).value, tag: queryTag, page: 1 })"
            >
              <option value="">{{ t('news.filterAll') }}</option>
              <option v-for="c in uniqueCategories" :key="c.slug" :value="c.slug">
                {{ c.name }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="uniqueTags.length" class="news-index__filters-row news-index__filters-row--secondary">
          <div class="news-index__filter">
            <label for="news-tag" class="news-index__label">Tag</label>
            <select
              id="news-tag"
              :value="queryTag"
              class="news-index__select"
              @change="applyFilters({ q: queryQ, category: queryCategory, tag: ($event.target as HTMLSelectElement).value, page: 1 })"
            >
              <option value="">{{ t('news.filterAll') }}</option>
              <option v-for="t in uniqueTags" :key="t.slug" :value="t.slug">
                {{ t.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <p v-if="totalFiltered === 0" class="news-index__empty">
      No articles found.
    </p>

    <div
      v-else
      class="news-index__toolbar"
    >
      <p class="news-index__results-count">
        {{
          t('news.showingRange', {
            from: showingFrom,
            to: showingTo,
            total: totalFiltered,
          })
        }}
      </p>
      <nav
        v-if="totalPages > 1"
        class="news-index__pagination"
        aria-label="News pagination"
      >
        <button
          type="button"
          class="news-index__page-btn"
          :disabled="currentPage <= 1"
          @click="
            applyFilters({
              q: queryQ,
              category: queryCategory,
              tag: queryTag,
              page: currentPage - 1,
            })
          "
        >
          {{ t('news.previous') }}
        </button>
        <span class="news-index__page-info">
          {{
            t('news.pageOf', {
              current: currentPage,
              total: totalPages,
            })
          }}
        </span>
        <button
          type="button"
          class="news-index__page-btn"
          :disabled="currentPage >= totalPages"
          @click="
            applyFilters({
              q: queryQ,
              category: queryCategory,
              tag: queryTag,
              page: currentPage + 1,
            })
          "
        >
          {{ t('news.next') }}
        </button>
      </nav>
    </div>

    <ul v-if="totalFiltered > 0" ref="newsIndexListRoot" class="news-index__list">
      <li
        v-for="item in paginatedArticles"
        :key="item.id"
        class="news-index__card"
      >
        <a :href="urlForItem(item)" class="news-index__link">
          <div class="news-index__media">
            <img
              v-if="imageUrl(item)"
              :src="imageUrl(item) ?? ''"
              :alt="imageAlt(item)"
              class="news-index__media-img"
              loading="lazy"
              width="800"
              height="600"
              @error="onOfflineMediaImgError"
            />
            <div
              v-else
              class="news-index__media-placeholder"
              aria-hidden="true"
            />
            <span v-if="item.category?.name" class="news-index__badge">
              <svg
                class="news-index__badge-icon"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7 7h.01"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {{ item.category.name }}
            </span>
          </div>
          <div class="news-index__content">
            <div
              v-if="item.rich_title && String(item.rich_title).trim()"
              class="news-index__title news-index__title--rich block-rich-text"
              v-html="normalizeWagtailRichTextHtml(item.rich_title)"
            />
            <span v-else class="news-index__title">{{ normalizeCmsPlainText(item.title) }}</span>
            <p v-if="item.meta?.search_description" class="news-index__desc">
              {{ normalizeCmsPlainText(item.meta.search_description) }}
            </p>
            <span v-if="item.tags?.length" class="news-index__tags">
              <span
                v-for="t in item.tags"
                :key="t.slug"
                class="news-index__tag"
              >{{ t.name }}</span>
            </span>
            <div
              v-if="item.date || readingMinutesById[item.id] != null"
              class="news-index__footer"
            >
              <div
                class="news-index__footer-meta"
                :class="{
                  'news-index__footer-meta--read-only':
                    !item.date && readingMinutesById[item.id] != null,
                }"
              >
                <span v-if="item.date" class="news-index__footer-date">
                  <svg
                    class="news-index__footer-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      stroke="currentColor"
                      stroke-width="2"
                    />
                    <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" stroke-width="2" />
                  </svg>
                  <time :datetime="item.date">{{ formatDate(item.date) }}</time>
                </span>
                <span
                  v-if="readingMinutesById[item.id] != null"
                  class="news-index__footer-read"
                >
                  <svg
                    class="news-index__footer-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  </svg>
                  {{ t('news.readingMinutes', { n: readingMinutesById[item.id]! }) }}
                </span>
              </div>
            </div>
          </div>
        </a>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.news-index {
  /* Spacing from header / quick links: PageView .page-view__header-group margin-bottom */
  margin-top: 0;
}

.news-index__featured {
  margin-bottom: 1.5rem;
}

@media (min-width: 640px) {
  .news-index__featured {
    margin-bottom: 2rem;
  }
}

/* Latest News filter card — aligned with INDMO prototype (LatestNews “Find News” block) */
.news-index__filters-card {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.75rem;
}

@media (min-width: 640px) {
  .news-index__filters-card {
    padding: 1.5rem;
    border-radius: 1rem;
  }
}

.news-index__filters-head {
  margin-bottom: 0.75rem;
}

@media (min-width: 640px) {
  .news-index__filters-head {
    margin-bottom: 1rem;
  }
}

.news-index__filters-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text, #1a1a1a);
}

@media (min-width: 640px) {
  .news-index__filters-title {
    font-size: 1.125rem;
  }
}

.news-index__filters-title-icon {
  flex-shrink: 0;
  display: block;
  color: var(--color-primary, #047857);
}

.news-index__filters-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.news-index__filters-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: stretch;
}

@media (min-width: 1024px) {
  .news-index__filters-row {
    flex-direction: row;
    align-items: flex-end;
  }

  .news-index__search {
    flex: 3 1 0;
    min-width: 0;
  }

  .news-index__filters-row > .news-index__filter {
    flex: 2 1 0;
    min-width: 0;
  }

  .news-index__filters-row--secondary > .news-index__filter {
    flex: 0 1 calc(40% - 0.5rem);
    max-width: 100%;
  }
}

.news-index__search {
  min-width: 0;
}

.news-index__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.news-index__input-wrap {
  position: relative;
}

.news-index__input-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: block;
  pointer-events: none;
  color: #9ca3af;
}

.news-index__input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  font-size: 0.875rem;
  line-height: 1.25;
  color: var(--color-text, #1a1a1a);
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: var(--color-surface, #fff);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.news-index__input::placeholder {
  color: #9ca3af;
}

.news-index__input:hover {
  border-color: #9ca3af;
}

.news-index__input:focus-visible {
  outline: none;
  border-color: var(--color-primary, #047857);
  box-shadow: 0 0 0 2px var(--color-focus-ring, rgba(4, 120, 87, 0.25));
}

.news-index__select {
  width: 100%;
  box-sizing: border-box;
  padding: 0.625rem 2.5rem 0.625rem 1rem;
  font-size: 0.875rem;
  line-height: 1.25;
  color: var(--color-text, #1a1a1a);
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: var(--color-surface, #fff);
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23666%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.25rem 1.25rem;
  appearance: none;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.news-index__select:hover {
  border-color: #9ca3af;
  cursor: pointer;
}

.news-index__select:focus-visible {
  outline: none;
  border-color: var(--color-primary, #047857);
  box-shadow: 0 0 0 2px var(--color-focus-ring, rgba(4, 120, 87, 0.25));
  cursor: pointer;
}

.news-index__filter {
  min-width: 0;
}

.news-index__empty {
  color: var(--color-text-muted, #666);
  margin: 1rem 0;
}

.news-index__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.news-index__results-count {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.25;
  color: var(--color-text-muted, #737373);
}

/* Latest News results — INDMO prototype LatestNews.tsx grid cards */
.news-index__list {
  list-style: none;
  margin: 0 0 1.5rem;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .news-index__list {
    margin-bottom: 2rem;
  }
}

@media (min-width: 1024px) {
  .news-index__list {
    margin-bottom: 2.5rem;
  }
}

@media (min-width: 1300px) {
  .news-index__list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1536px) {
  .news-index__list {
    grid-template-columns: repeat(3, 1fr);
  }
}

.news-index__card {
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.75rem;
  overflow: hidden;
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;
}

.news-index__card:hover {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  transform: translateY(-0.25rem);
}

.news-index__link {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  text-decoration: none;
  color: inherit;
}

.news-index__link:hover .news-index__title {
  color: var(--color-primary, #047857);
}

.news-index__media {
  position: relative;
  flex-shrink: 0;
  height: 12rem;
  background: var(--color-bg-muted, #f3f4f6);
}

.news-index__media-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-index__media-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    var(--color-bg-muted, #f3f4f6) 0%,
    #e5e7eb 100%
  );
}

.news-index__badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  color: #1d4ed8;
  background: #dbeafe;
  border-radius: 0.5rem;
}

.news-index__badge-icon {
  flex-shrink: 0;
  display: block;
}

.news-index__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.25rem;
}

.news-index__title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0 0 0.5rem;
  transition: color 0.2s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-index__title--rich.block-rich-text :deep(.indmo-richtext-primary) {
  color: var(--color-primary);
}

.news-index__title--rich.block-rich-text :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.15em;
  color: var(--color-primary);
}

.news-index__title--rich.block-rich-text :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}

.news-index__title--rich.block-rich-text :deep(p) {
  margin: 0;
}

.news-index__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin: 0 0 0.75rem;
}

.news-index__tag {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  color: var(--color-text-muted, #525252);
  background: var(--color-bg-muted, #f3f4f6);
}

.news-index__desc {
  margin: 0 0 1rem;
  flex: 1 1 auto;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-text-muted, #525252);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-index__footer {
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border, #e5e7eb);
  font-size: 0.75rem;
  color: var(--color-text-muted, #525252);
}

.news-index__footer-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  width: 100%;
}

.news-index__footer-meta--read-only {
  justify-content: flex-end;
}

.news-index__footer-date {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.news-index__footer-read {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

.news-index__footer-read .news-index__footer-icon {
  flex-shrink: 0;
  display: block;
  opacity: 0.85;
}

.news-index__footer-icon {
  flex-shrink: 0;
  display: block;
  opacity: 0.85;
}

.news-index__pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin: 0;
}

.news-index__page-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  cursor: pointer;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 0.5rem;
  background: var(--color-surface, #fff);
  color: var(--color-text, #1a1a1a);
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.news-index__page-btn:hover:not(:disabled) {
  border-color: var(--color-primary, #047857);
  background: var(--color-bg-muted, #f9fafb);
}

.news-index__page-btn:focus-visible {
  outline: none;
  border-color: var(--color-primary, #047857);
  box-shadow: 0 0 0 2px var(--color-focus-ring, rgba(4, 120, 87, 0.25));
}

.news-index__page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.news-index__page-info {
  font-size: 0.875rem;
  color: var(--color-text-muted, #737373);
  padding: 0 0.25rem;
  white-space: nowrap;
}
</style>
